import { GuidedField } from '../../guidance/GuidedField';
import { fieldDef } from '../../guidance/roleFieldRegistry';
import { GroupedCombobox, type ComboLoadResult } from '../guided/GroupedCombobox';
import { useWizard } from './WizardContext';

/**
 * Step 1: start from an EPN template (a searchable list of the catalog, showing the title, SOC code and family) or from a blank role.
 * A template is EPN-written starting text that the company then owns: choosing one here only shows what it contains. Nothing is copied into the
 * role until the person chooses Next, and the template itself is never changed.
 */
export function StartStep() {
  const w = useWizard();
  const startDef = fieldDef('startMode');
  const tplDef = fieldDef('template');
  const chosen = w.templates?.find((t) => t.id === w.templateId) ?? null;
  const pending = w.pendingTemplate;

  async function load(query: string): Promise<ComboLoadResult> {
    const q = query.toLowerCase();
    const list = (w.templates ?? []).filter((t) => !q || [t.title, t.code, t.socCode ?? '', t.jobFamily ?? '', t.careerLevel ?? '', t.suggestedDepartmentName ?? ''].some((v) => v.toLowerCase().includes(q)));
    return {
      groups: list.length === 0 ? [] : [{
        key: 'templates', label: 'EPN templates',
        options: list.slice(0, 50).map((t) => ({
          value: t.id, label: t.title, detail: [t.socCode ? `SOC ${t.socCode}` : null, t.jobFamily, t.careerLevel].filter(Boolean).join(' · ') || null,
        })),
      }],
      notice: list.length > 50 ? `Showing the first 50 of ${list.length}. Type to narrow the list.` : null,
    };
  }

  return (
    <div className="jr-step">
      <GuidedField field={startDef} labelMode="group" hideBadge>
        {() => (
          <div className="jr-choice jr-choice--plain">
            <label>
              <input type="radio" name={`${w.uid}-start`} checked={w.startMode === 'template'} onChange={() => w.switchStartMode('template')} />
              Use an EPN template
            </label>
            <label>
              <input type="radio" name={`${w.uid}-start`} checked={w.startMode === 'blank'} onChange={() => w.switchStartMode('blank')} />
              Start from a blank role
            </label>
          </div>
        )}
      </GuidedField>

      {w.startMode === 'template' && (
        <div className="jr-templates">
          <p className="jr-hint">
            A template is EPN-written starting text that you then own. A copy becomes your role, so changing your copy never changes the EPN template, and a
            template never sets anyone&apos;s pay. Nothing is copied until you choose Next.
          </p>
          {w.templatesError && <p className="jr-error" role="alert">{w.templatesError}</p>}
          {w.templates === null && !w.templatesError && <p className="muted" role="status">Loading templates{'…'}</p>}
          {w.templatesLocked && <p className="muted">The EPN template library is not part of your current plan. You can still start from a blank role.</p>}
          {w.templates !== null && w.templates.length === 0 && !w.templatesLocked && <p className="muted">No templates are available.</p>}
          {w.templates !== null && w.templates.length > 0 && (
            <GuidedField field={tplDef} error={w.templateError ?? undefined} hideBadge>
              {(a) => (
                <GroupedCombobox
                  a11y={{ ...a, 'data-field': 'template' }}
                  mode="select"
                  value={w.templateId ?? ''}
                  valueLabel={chosen?.title ?? ''}
                  disabled={w.templateBusy}
                  load={load}
                  onSelect={(o) => { const t = w.templates?.find((x) => x.id === o.value); if (t) w.chooseTemplate(t); }}
                  onClear={w.clearTemplate}
                  clearLabel="template"
                  placeholder="Search by title, SOC code or job family"
                  emptyText="No template matches. Try another word, or start from a blank role."
                />
              )}
            </GuidedField>
          )}

          {pending && (
            <section className="jr-card jr-template-preview" aria-label="Selected template" data-testid="template-preview">
              <h5 className="jr-card__title">{pending.title}</h5>
              <p className="muted">{[pending.socCode ? `SOC ${pending.socCode}` : null, pending.jobFamily, pending.careerLevel].filter(Boolean).join(' · ')}</p>
              {pending.content.summary && <p>{pending.content.summary}</p>}
              <p className="jr-hint">
                It carries {pending.content.essentialDuties.length} essential {pending.content.essentialDuties.length === 1 ? 'duty' : 'duties'} and
                {' '}{pending.content.qualifications.technicalSkills.length + pending.content.qualifications.professionalSkills.length} skills as starting text.
                Choose Next to copy it into your own role; you can change all of it.
              </p>
            </section>
          )}
        </div>
      )}
      {w.startMode === 'blank' && <p className="muted">You will describe the role yourself on the next steps. You can save a draft at any time.</p>}
    </div>
  );
}
